import { computed, nextTick, ref, Ref, watch, WatchStopHandle } from 'vue';
import { IMultiSelect } from '@studyportals/multiselect/dist/src/interfaces/multiselect.interface';
import { IOption } from '@studyportals/multiselect/dist/src/interfaces/options.interface';
import { PartnershipType } from '@studyportals/campaign-management-api-interface';
import { ValidationProperties } from '@/models/validation.interface';
import { LoadingStatuses } from '@/models/loading.interface';
import validationPresenter from '@/presenters/validation-presenter';
import store from '@/store';

export default class PartnershipTypeCode {
	public partnershipTypeDropdown: Ref<IMultiSelect | null> = ref(null);
	public options: IOption[] = [
		{ label: 'RBP', value: PartnershipType.RBP },
		{ label: 'Non-RBP', value: PartnershipType.NON_RBP },
		{ label: 'Hybrid EBP', value: PartnershipType.HYBRID_EBP },
		{ label: 'Full EBP', value: PartnershipType.FULL_EBP },
		{ label: 'Free Trial', value: PartnershipType.FREE_TRIAL }
	];
	private unwatch: WatchStopHandle | null = null;

	public nameOfSelectedPartnershipType = computed((): string => {
		const selectedOption = this.options.find((option) => option.value === this.partnershipType.value);
		return selectedOption ? selectedOption.label : 'Not available';
	});

	public isCampaignCreation = computed((): boolean => {
		return store.getters.isCampaignCreation();
	});

	public partnershipType = computed((): PartnershipType | null => {
		return store.getters.partnershipType();
	});

	public isLoading = computed((): boolean => {
		const loadingStatus = store.getters.loadingStatusPrimaryData();
		return loadingStatus === LoadingStatuses.ACTIVE;
	});

	public validationMessage = computed((): string => {
		if (this.isFieldValidatedAndMissing.value) {
			return 'This field must be completed before proceeding';
		}

		return 'This partnership type does not allow campaign lift exclusion';
	});

	public isFieldClashingWithCampaignLiftExcluded = computed((): boolean => {
		return validationPresenter.isPropertyInvalid(ValidationProperties.PARTNERSHIP_TYPE);
	});

	public isFieldValidatedAndMissing = computed((): boolean => {
		return validationPresenter.isPropertyMissing(ValidationProperties.PARTNERSHIP_TYPE);
	});

	private isPartnershipTypeSet = computed((): boolean => {
		return this.partnershipType.value !== null;
	});

	public mounted(): void {
		this.unwatch = watch(this.isLoading, this.prefillPartnershipType.bind(this));
		void this.prefillPartnershipType();
	}

	public unmounted(): void {
		if (!this.unwatch) {
			return;
		}

		this.unwatch();
	}

	public setPartnershipType(option: IOption): void {
		const newPartnershipType = option.value as PartnershipType;
		store.mutations.setPartnershipType(newPartnershipType);

		if (!this.isFieldValidatedAndMissing.value && !this.isFieldClashingWithCampaignLiftExcluded.value) {
			return;
		}

		validationPresenter.validateMandatoryFields();
	}

	private async prefillPartnershipType(): Promise<void> {
		if (this.isLoading.value) {
			return;
		}

		await nextTick();

		if (!this.partnershipTypeDropdown.value || !this.isPartnershipTypeSet.value) {
			return;
		}

		const selectedOption = this.options.find((option) => option.value === this.partnershipType.value);
		if (!selectedOption) {
			return;
		}

		this.partnershipTypeDropdown.value.selectOption(selectedOption);
	}
}
